import { useMutation } from "@tanstack/react-query";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { NewClaimRequest } from "~/shared/schemas/shipment-management-schema.ts";
import { shipmentClaimResponseSchema } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

import type { ClaimsFormData } from "../utils/constants.ts";

export const toNewClaimRequest = (form: ClaimsFormData): NewClaimRequest => ({
	// create expected API format
	carrierId: form.carrier?.carrierId ?? form.carrier?.id ?? 0,
	claimReasonId: form.claimReason,
	overFreight: form.claimExceedPayAmount === "yes",
	estimatedValue: form.estimatedClaimAmount,
	actualValue: form.actualClaimAmount,
	salvageAmount: form.salvageAmount,
	federalInspection: form.fedInspection === "yes",
	tempRecording: form.tempRecorder === "yes",
	salvageHelp: form.needHelpWithSalvage === "yes",
	customerContactName: form.contactName,
	customerContactEmail: form.contactEmail,
	customerContactPhone: form.contactPhone,
	customerContactPhoneExt: form.contactExtension,
	claimDescription: form.claimDetails,
});

export const postNewClaim = async (form: ClaimsFormData) => {
	const url = apiRoutes.POSTNEWCLAIM.replace(":poNumber", form.poNumber);
	const body = toNewClaimRequest(form);
	const resp = await postAndValidate(url, body, shipmentClaimResponseSchema);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostNewClaim = () => {
	const { showSuccess, showError } = useShipmentNotifications();
	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (form: ClaimsFormData) => postNewClaim(form),
		onSuccess: () => {
			showSuccess(SHIPMENT_TOAST_MESSAGES.claimSaved);
		},
		onError: () => {
			showError("Failed to submit claim. Please try again.");
		},
	});

	return {
		postNewClaim: mutate,
		postNewClaimAsync: mutateAsync,
		isPostingNewClaim: isPending,
		error,
		data,
		resetError: reset,
	};
};
